import { z } from 'zod';

/**
 * Описание таблицы для ридера (GET /projects/:id/files/:fileId/sheet). Источник — server/src/services/filePreview/sheetModel.ts:
 * при изменении там меняется и SHEET_MODEL_VERSION, и эта схема. Ответ проверяется на входе: ридер не рисует то, чего не ждёт.
 */
export const SHEET_MODEL_VERSION = 1;

const color = z.string().regex(/^#[0-9a-f]{6}$/i);
const borderSide = z.object({
  style: z.enum(['thin', 'medium', 'thick', 'dashed', 'dotted', 'double', 'hair']),
  color,
});

export const sheetCellStyleSchema = z.object({
  bold: z.literal(true).optional(),
  italic: z.literal(true).optional(),
  underline: z.literal(true).optional(),
  strike: z.literal(true).optional(),
  color: color.optional(),
  fill: color.optional(),
  fontSize: z.number().positive().optional(),
  fontName: z.string().optional(),
  hAlign: z.enum(['left', 'center', 'right', 'justify', 'fill', 'centerContinuous', 'distributed']).optional(),
  vAlign: z.enum(['top', 'middle', 'bottom', 'justify', 'distributed']).optional(),
  wrap: z.literal(true).optional(),
  indent: z.number().nonnegative().optional(),
  borders: z
    .object({ top: borderSide.optional(), right: borderSide.optional(), bottom: borderSide.optional(), left: borderSide.optional() })
    .optional(),
});

const index = z.number().int().nonnegative();

export const sheetCellSchema = z.object({
  r: index,
  c: index,
  text: z.string(),
  kind: z.enum(['text', 'number', 'date', 'bool', 'error', 'empty']),
  s: index.optional(),
  formula: z.string().optional(),
  link: z.string().optional(),
});

export const sheetDataSchema = z.object({
  name: z.string(),
  rowCount: index,
  colCount: index,
  colWidths: z.array(z.number().nonnegative()),
  rowHeights: z.array(z.number().nonnegative()),
  cells: z.array(sheetCellSchema),
  merges: z.array(z.object({ r1: index, c1: index, r2: index, c2: index })),
  frozen: z.object({ rows: index, cols: index }),
  gridLines: z.boolean(),
  truncatedFrom: index.optional(),
});

export const sheetWorkbookSchema = z.object({
  version: z.literal(SHEET_MODEL_VERSION),
  activeSheet: index,
  sheets: z.array(sheetDataSchema),
  styles: z.array(sheetCellStyleSchema),
});

export type SheetBorderSide = z.infer<typeof borderSide>;
export type SheetCellStyle = z.infer<typeof sheetCellStyleSchema>;
export type SheetCell = z.infer<typeof sheetCellSchema>;
export type SheetData = z.infer<typeof sheetDataSchema>;
export type SheetWorkbook = z.infer<typeof sheetWorkbookSchema>;
